import { Component, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import { OrderService } from '../../_services/order.service';
import { DeliveryMethod, PaymentMethod, RefundMethod, SiteSettings, SiteSettingsService } from '../../_services/site-settings.service';

@Component({
    selector: 'app-site-settings',
    imports: [MainLayoutComponent, CommonModule, FormsModule, MatIconModule],
    templateUrl: './site-settings.component.html'
})
export class SiteSettingsComponent implements OnInit {
  loading = true;
  saving = false;
  settings: SiteSettings | null = null;
  private savedJson = '';

  readonly deliveryOptions: { value: DeliveryMethod; label: string; hint: string; icon: string }[] = [
    { value: 'STANDARD', label: 'Standard', hint: 'Usual delivery times', icon: 'local_shipping' },
    { value: 'EXPRESS', label: 'Express', hint: 'Faster delivery, extra charge', icon: 'bolt' },
    { value: 'NEXTDAY', label: 'Next day', hint: 'Delivered the next day, extra charge', icon: 'event_available' },
    { value: 'COLLECTION', label: 'Collection', hint: 'Customer collects from the yard', icon: 'storefront' },
  ];

  // websiteToo false: offered in the back office only.
  readonly paymentOptions: { value: PaymentMethod; label: string; hint: string; icon: string; websiteToo: boolean }[] = [
    { value: 'TEYA', label: 'Card (Teya)', hint: 'Visa, Mastercard, Amex', icon: 'credit_card', websiteToo: true },
    { value: 'PAYPAL', label: 'PayPal', hint: 'Including Pay in 3', icon: 'account_balance_wallet', websiteToo: true },
    { value: 'BANK_TRANSFER', label: 'Bank transfer', hint: 'Order is held until the money arrives', icon: 'account_balance', websiteToo: true },
    { value: 'INVOICE', label: 'Invoice', hint: 'Customer is emailed a payment link', icon: 'receipt_long', websiteToo: false },
  ];

  readonly refundOptions: { value: RefundMethod; label: string; hint: string }[] = [
    { value: 'PAYPAL', label: 'PayPal', hint: 'Refund PayPal orders back to the customer through PayPal' },
    { value: 'TEYA', label: 'Teya (card)', hint: 'Refund card orders back to the customer through Teya' },
  ];

  constructor(
    private siteSettingsService: SiteSettingsService,
    private alertService: AlertService,
    private orderService: OrderService,
  ) {}

  // ---- PayPal tracking catch-up ----
  // Defaults to this month - PayPal's instant-money limit is per month.
  resyncFrom = formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd', 'en');
  resyncTo = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  resyncBusy = false;
  resync: any = null;

  readonly resyncActionLabel: Record<string, string> = {
    send: 'Will send',
    retry: 'Will retry',
    'already-sent': 'Already at PayPal',
    'skip-no-payment-ref': 'No payment ref - skipped',
    'skip-collection': 'Collection - skipped',
  };

  // Orders still to send: all of them after a check, what's left after a batch.
  get resyncPending(): number {
    if (!this.resync) return 0;
    return this.resync.dryRun ? this.resync.toSend + this.resync.toRetry : this.resync.remaining;
  }

  runResync(dryRun: boolean): void {
    if (this.resyncBusy) return;
    const batch = Math.min(this.resyncPending, this.resync?.batchSize ?? 20);
    if (!dryRun && !confirm(`Send tracking to PayPal for the next ${batch} of ${this.resyncPending} order(s)? Each goes through the normal flow (Sent now, then In transit and Delivered on the usual timer).`)) return;
    this.resyncBusy = true;
    this.orderService.resyncPaypalTracking({ from: this.resyncFrom, to: this.resyncTo, dryRun }).subscribe({
      next: (r) => {
        this.resyncBusy = false;
        this.resync = r;
        if (!dryRun) {
          this.alertService.alert(
            `Sent ${r.sent} to PayPal${r.failed ? `, ${r.failed} failed` : ''}${r.remaining ? ` - ${r.remaining} left, press Send again` : ''}`,
            r.failed ? 'error' : 'success',
          );
        }
      },
      error: (e) => {
        this.resyncBusy = false;
        this.alertService.alert(e?.error?.message || 'Could not check PayPal tracking', 'error');
      },
    });
  }

  // A preview is only valid for the dates it was run with.
  onResyncDates(): void {
    this.resync = null;
  }

  ngOnInit(): void {
    this.siteSettingsService.getForOwner().subscribe({
      next: (s) => this.setSettings(s),
      error: () => {
        this.loading = false;
        this.alertService.alert('Could not load the site settings', 'error');
      },
    });
  }

  get dirty(): boolean {
    return !!this.settings && this.payload() !== this.savedJson;
  }

  // Each channel needs something customers can pick.
  channelHasOption(channel: 'website' | 'backOffice'): boolean {
    return !!this.settings && this.deliveryOptions.some((o) => this.settings!.delivery[o.value][channel]);
  }

  channelHasPayment(channel: 'website' | 'backOffice'): boolean {
    return !!this.settings?.payments && this.paymentOptions.some((o) => this.settings!.payments![o.value][channel]);
  }

  get canSave(): boolean {
    return (
      this.dirty && !this.saving &&
      this.channelHasOption('website') && this.channelHasOption('backOffice') &&
      this.channelHasPayment('website') && this.channelHasPayment('backOffice')
    );
  }

  save(): void {
    if (!this.settings || !this.canSave) return;
    this.saving = true;
    this.siteSettingsService.save(this.settings).subscribe({
      next: (s) => {
        this.saving = false;
        this.setSettings(s);
        this.alertService.alert('Site settings saved', 'success');
      },
      error: (e) => {
        this.saving = false;
        this.alertService.alert(e?.error?.message || 'Could not save the site settings', 'error');
      },
    });
  }

  reset(): void {
    if (this.savedJson) this.settings = { ...this.settings!, ...JSON.parse(this.savedJson) };
  }

  private setSettings(s: SiteSettings): void {
    // An older backend has no payment switches yet: start with everything on.
    if (!s.payments) {
      s.payments = {
        TEYA: { website: true, backOffice: true },
        PAYPAL: { website: true, backOffice: true },
        BANK_TRANSFER: { website: true, backOffice: true },
        INVOICE: { website: false, backOffice: true },
      };
    }
    this.settings = s;
    this.savedJson = this.payload();
    this.loading = false;
  }

  private payload(): string {
    return this.settings ? JSON.stringify({ delivery: this.settings.delivery, payments: this.settings.payments, refunds: this.settings.refunds }) : '';
  }
}
